import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Loading from '../ui/Loading';
import safeStorage from '../../utils/safeStorage';
import { setPlatform } from '../../utils/platform';

/**
 * Wix landing page — where the backend drops the browser after it has verified
 * the signed Wix `instance` and minted a Sanctum token.
 *
 * Reached as: /wix?token=…&platform=wix&embedded=1&locale=en
 *
 * This runs BEFORE the normal auth gate: a Wix site owner has no ZonStaff
 * password (their account email is synthetic), so the login screen would be a
 * dead end for them. All it does is adopt the token and hand over to the app.
 */
const WixEmbedded = ({ onLogin }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const handled = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const embedded = params.get('embedded') === '1';
    const locale = params.get('locale');

    if (embedded) {
      // Lets CSS hide our own chrome when Wix already supplies a sidebar.
      try {
        document.documentElement.dataset.embedded = '1';
      } catch { /* non-fatal */ }
    }

    if (locale) {
      // Wix passes the site's locale (e.g. "en", "de", "pt-BR").
      try {
        i18n.changeLanguage(String(locale).split('-')[0]);
      } catch { /* fall back to default language */ }
    }

    if (!token) {
      setError(
        'This page is opened from your Wix dashboard. No sign-in token was found — please reopen ZonStaff from the Wix dashboard.'
      );
      return;
    }

    safeStorage.set('authToken', token);
    // Records the platform so pricing/upgrade surfaces stay hidden: Wix requires
    // its own billing system and forbids third-party unlock mechanisms.
    setPlatform('wix');

    // Strip the token from the address bar so it is not left in history.
    try {
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch { /* non-fatal */ }

    onLogin?.(token, { platform: 'wix', embedded });
    navigate('/', { replace: true });
  }, [i18n, navigate, onLogin]);

  if (error) {
    return (
      <div
        style={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <div style={{ maxWidth: 520, textAlign: 'center' }}>
          <AlertCircle size={28} style={{ color: '#c02637', marginBottom: 12 }} />
          <h1 style={{ fontSize: 20, margin: '0 0 10px' }}>Couldn&apos;t open ZonStaff</h1>
          <p style={{ fontSize: 15, lineHeight: 1.55, color: '#5a6672', margin: 0 }}>{error}</p>
        </div>
      </div>
    );
  }

  return <Loading />;
};

export default WixEmbedded;
